import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api';
import {
  MessageSquare,
  Send,
  Search,
  MoreVertical,
  Paperclip,
  Image,
  Smile,
  X,
  Phone,
  Video,
  Users,
  Pin,
  BellOff,
  Archive,
  Trash2,
  Edit3,
  Reply,
  Check,
  CheckCheck,
  ChevronLeft
} from 'lucide-react';

/**
 * Main Chat Component - Full-featured chat engine
 * Supports: Brand-creator, creator-creator communications
 * Features: Direct messages, Group chats, Project chats, File sharing, Reactions
 */
const ChatEngine = ({ isOpen, onClose, initialChatRoomId = null }) => {
  const { user } = useAuth();
  const [chatRooms, setChatRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showRoomSettings, setShowRoomSettings] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [replyTo, setReplyTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showChatList, setShowChatList] = useState(true);

  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Common emoji reactions
  const commonEmojis = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

  // Fetch chat rooms
  const fetchChatRooms = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const response = await api.get(`/chat/rooms?userId=${user.userId}`);
      setChatRooms(response.data);

      // Calculate total unread
      const totalUnread = response.data.reduce((acc, room) => acc + (room.unreadCount || 0), 0);
      setUnreadCount(totalUnread);
    } catch (error) {
      // Error handled silently
    }
  }, [user?.userId]);

  // Fetch messages for a room
  const fetchMessages = useCallback(async (roomId) => {
    if (!user?.userId || !roomId) return;
    try {
      setLoading(true);
      const response = await api.get(`/chat/rooms/${roomId}/messages?userId=${user.userId}`);
      setMessages(response.data.reverse()); // Reverse to show oldest first

      // Mark as read
      await api.post(`/chat/rooms/${roomId}/read?userId=${user.userId}`);

      // Update unread count in chat rooms
      setChatRooms(prev => prev.map(room =>
        room.id === roomId ? { ...room, unreadCount: 0 } : room
      ));
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [user?.userId]);

  // Poll for new messages
  const pollNewMessages = useCallback(async () => {
    if (!selectedRoom || !user?.userId) return;

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage) return;

    try {
      const response = await api.get(
        `/chat/rooms/${selectedRoom.id}/messages/after?userId=${user.userId}&after=${lastMessage.sentAt}`
      );

      if (response.data.length > 0) {
        setMessages(prev => [...prev, ...response.data]);
        scrollToBottom();
      }
    } catch (error) {
      // Error handled silently
    }
  }, [selectedRoom, user?.userId, messages]);

  // Send message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedRoom || !user?.userId) return;

    try {
      const payload = {
        content: newMessage,
        messageType: 'TEXT',
        replyToId: replyTo?.id || null
      };

      if (editingMessage) {
        // Edit existing message
        await api.put(`/chat/messages/${editingMessage.id}?userId=${user.userId}`, {
          content: newMessage
        });
        setMessages(prev => prev.map(m =>
          m.id === editingMessage.id ? { ...m, content: newMessage, isEdited: true } : m
        ));
        setEditingMessage(null);
      } else {
        // Send new message
        const response = await api.post(
          `/chat/rooms/${selectedRoom.id}/messages?userId=${user.userId}`,
          payload
        );
        setMessages(prev => [...prev, response.data]);
      }

      setNewMessage('');
      setReplyTo(null);
      scrollToBottom();

      // Update chat room list
      fetchChatRooms();
    } catch (error) {
      // Error handled silently
    }
  };

  // Delete message
  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/chat/messages/${messageId}?userId=${user.userId}`);
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, isDeleted: true, content: 'This message was deleted' } : m
      ));
    } catch (error) {
      // Error handled silently
    }
  };

  // Add reaction
  const addReaction = async (messageId, emoji) => {
    try {
      const response = await api.post(
        `/chat/messages/${messageId}/reactions?userId=${user.userId}`,
        { emoji }
      );
      setMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, reactions: response.data.reactions } : m
      ));
    } catch (error) {
      // Error handled silently
    }
  };

  // Start direct chat
  const startDirectChat = async (targetUserId, initialMessage = '') => {
    try {
      const response = await api.post(
        `/chat/rooms/direct?userId=${user.userId}`,
        { targetUserId, initialMessage }
      );
      setChatRooms(prev => {
        const exists = prev.find(r => r.id === response.data.id);
        if (exists) return prev;
        return [response.data, ...prev];
      });
      setSelectedRoom(response.data);
      setShowNewChatModal(false);
      fetchMessages(response.data.id);
    } catch (error) {
      // Error handled silently
    }
  };

  // Create group chat
  const createGroupChat = async (name, participantIds) => {
    try {
      const response = await api.post(
        `/chat/rooms/group?userId=${user.userId}`,
        { name, participantIds, type: 'GROUP' }
      );
      setChatRooms(prev => [response.data, ...prev]);
      setSelectedRoom(response.data);
      setShowNewChatModal(false);
      fetchMessages(response.data.id);
    } catch (error) {
      // Error handled silently
    }
  };

  // Update room settings
  const updateRoomSettings = async (settings) => {
    if (!selectedRoom) return;
    try {
      await api.put(
        `/chat/rooms/${selectedRoom.id}/settings?userId=${user.userId}`,
        settings
      );
      setChatRooms(prev => prev.map(room =>
        room.id === selectedRoom.id ? { ...room, ...settings } : room
      ));
      setShowRoomSettings(false);
    } catch (error) {
      // Error handled silently
    }
  };

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedRoom) return;

    // For now, just show a placeholder - integrate with your file upload service
    // TODO: Implement file upload to S3/Cloudinary
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle keyboard shortcuts
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen && user?.userId) {
      fetchChatRooms();
    }
  }, [isOpen, user?.userId, fetchChatRooms]);

  // Load initial chat room if provided
  useEffect(() => {
    if (initialChatRoomId && chatRooms.length > 0) {
      const room = chatRooms.find(r => r.id === initialChatRoomId);
      if (room) {
        setSelectedRoom(room);
        fetchMessages(room.id);
      }
    }
  }, [initialChatRoomId, chatRooms, fetchMessages]);

  // Poll for new messages
  useEffect(() => {
    if (selectedRoom) {
      pollIntervalRef.current = setInterval(pollNewMessages, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [selectedRoom, pollNewMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Filter chat rooms by search
  const filteredRooms = chatRooms.filter(room =>
    room.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[1000] flex items-center justify-center md:p-4">
      <div className="bg-white shadow-2xl w-full h-full md:max-w-6xl md:h-[85vh] md:rounded-lg flex overflow-hidden">

        {/* Chat List Sidebar */}
        {(!isMobileView || showChatList) && (
          <div className="w-full md:w-80 border-r border-gray-200 flex flex-col bg-gray-50">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-collaborb-primary" />
                  Messages
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-collaborb-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* New Chat Button */}
            <button
              onClick={() => setShowNewChatModal(true)}
              className="m-4 py-2 px-4 bg-collaborb-primary text-white rounded-lg hover:bg-collaborb-primary-dark transition flex items-center justify-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              New Conversation
            </button>

            {/* Chat Room List */}
            <div className="flex-1 overflow-y-auto">
              {filteredRooms.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  No conversations yet
                </div>
              ) : (
                filteredRooms.map(room => (
                  <div
                    key={room.id}
                    onClick={() => {
                      setSelectedRoom(room);
                      fetchMessages(room.id);
                      if (isMobileView) setShowChatList(false);
                    }}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-white transition ${
                      selectedRoom?.id === room.id ? 'bg-white border-l-4 border-l-collaborb-primary' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Avatar */}
                      <div className="relative">
                        {room.avatarUrl ? (
                          <img
                            src={room.avatarUrl}
                            alt={room.name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-collaborb-primary flex items-center justify-center text-white font-semibold">
                            {room.name?.charAt(0)?.toUpperCase() || '?'}
                          </div>
                        )}
                        {room.type === 'GROUP' && (
                          <Users className="absolute -bottom-1 -right-1 w-4 h-4 text-collaborb-primary bg-white rounded-full" />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold text-gray-800 truncate flex items-center gap-1">
                            {room.isPinned && <Pin className="w-3 h-3 text-collaborb-primary" />}
                            {room.isMuted && <BellOff className="w-3 h-3 text-gray-400" />}
                            {room.name}
                          </h3>
                          <span className="text-xs text-gray-500">
                            {formatTime(room.lastMessageAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {room.lastMessage?.content || 'No messages yet'}
                        </p>
                      </div>

                      {/* Unread Badge */}
                      {room.unreadCount > 0 && (
                        <span className="bg-collaborb-primary text-white text-xs px-2 py-0.5 rounded-full">
                          {room.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Chat Area */}
        {(!isMobileView || !showChatList) && (
          <div className="flex-1 flex flex-col">
            {selectedRoom ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isMobileView && (
                      <button
                        onClick={() => setShowChatList(true)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}
                    {selectedRoom.avatarUrl ? (
                      <img
                        src={selectedRoom.avatarUrl}
                        alt={selectedRoom.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-collaborb-primary flex items-center justify-center text-white font-semibold">
                        {selectedRoom.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-800">{selectedRoom.name}</h3>
                      <p className="text-xs text-gray-500">
                        {selectedRoom.type === 'GROUP'
                          ? `${selectedRoom.participants?.length || 0} members`
                          : selectedRoom.participants?.find(p => p.userId !== user?.userId)?.role || ''
                        }
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button className="p-2 hover:bg-gray-100 rounded-full" title="Voice Call">
                      <Phone className="w-5 h-5 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-100 rounded-full" title="Video Call">
                      <Video className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => setShowRoomSettings(true)}
                      className="p-2 hover:bg-gray-100 rounded-full"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-collaborb-primary"></div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <MessageSquare className="w-16 h-16 mb-4 text-gray-300" />
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((message, index) => {
                        const isOwn = message.isOwnMessage || message.senderId === user?.userId;
                        const showAvatar = index === 0 || messages[index - 1]?.senderId !== message.senderId;

                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`flex gap-2 max-w-[70%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                              {/* Avatar */}
                              {!isOwn && showAvatar && (
                                <img
                                  src={message.senderAvatar || '/default-avatar.png'}
                                  alt={message.senderName}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              )}
                              {!isOwn && !showAvatar && <div className="w-8" />}

                              {/* Message Bubble */}
                              <div className="group relative">
                                {/* Reply Preview */}
                                {message.replyToId && (
                                  <div className={`text-xs px-3 py-1 mb-1 rounded-t-lg ${
                                    isOwn ? 'bg-collaborb-primary-light text-gray-700' : 'bg-gray-200 text-gray-700'
                                  }`}>
                                    <span className="font-medium">{message.replyToSenderName}</span>
                                    <p className="truncate">{message.replyToContent}</p>
                                  </div>
                                )}

                                {/* Message Content */}
                                <div
                                  className={`px-4 py-2 rounded-2xl ${
                                    message.isDeleted 
                                      ? 'bg-gray-200 text-gray-500 italic'
                                      : isOwn
                                        ? 'bg-collaborb-primary text-white rounded-br-md'
                                        : 'bg-white text-gray-800 rounded-bl-md shadow-sm'
                                  }`}
                                >
                                  {message.messageType === 'SYSTEM' ? (
                                    <p className="text-center text-sm text-gray-500 italic">
                                      {message.content}
                                    </p>
                                  ) : (
                                    <>
                                      {!isOwn && showAvatar && (
                                        <p className="text-xs font-medium text-collaborb-primary mb-1">
                                          {message.senderName}
                                        </p>
                                      )}
                                      <p className="whitespace-pre-wrap break-words">{message.content}</p>

                                      {/* Attachment Preview */}
                                      {message.attachmentUrl && (
                                        <div className="mt-2">
                                          {message.messageType === 'IMAGE' ? (
                                            <img
                                              src={message.attachmentUrl}
                                              alt="attachment"
                                              className="max-w-full rounded-lg"
                                            />
                                          ) : (
                                            <a
                                              href={message.attachmentUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center gap-2 text-sm underline"
                                            >
                                              <Paperclip className="w-4 h-4" />
                                              {message.attachmentName}
                                            </a>
                                          )}
                                        </div>
                                      )}

                                      <div className={`flex items-center gap-1 mt-1 text-xs ${
                                        isOwn ? 'text-white/70' : 'text-gray-400'
                                      }`}>
                                        <span>{formatTime(message.sentAt)}</span>
                                        {message.isEdited && <span>(edited)</span>}
                                        {isOwn && (
                                          message.readByUserIds?.length > 0
                                            ? <CheckCheck className="w-3 h-3" />
                                            : <Check className="w-3 h-3" />
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>

                                {/* Reactions */}
                                {message.reactions && Object.keys(message.reactions).length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {Object.entries(message.reactions).map(([emoji, userIds]) => (
                                      <button
                                        key={emoji}
                                        onClick={() => addReaction(message.id, emoji)}
                                        className="bg-gray-100 hover:bg-gray-200 px-2 py-0.5 rounded-full text-sm flex items-center gap-1"
                                      >
                                        <span>{emoji}</span>
                                        <span className="text-xs text-gray-500">{userIds.length}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}

                                {/* Message Actions */}
                                {!message.isDeleted && message.messageType !== 'SYSTEM' && (
                                  <div className={`absolute top-0 ${isOwn ? 'left-0 -translate-x-full pr-2' : 'right-0 translate-x-full pl-2'} opacity-0 group-hover:opacity-100 transition flex items-center gap-1`}>
                                    <button
                                      onClick={() => setShowEmojiPicker(message.id)}
                                      className="p-1 hover:bg-gray-100 rounded"
                                      title="Add reaction"
                                    >
                                      <Smile className="w-4 h-4 text-gray-500" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setReplyTo(message);
                                        messageInputRef.current?.focus();
                                      }}
                                      className="p-1 hover:bg-gray-100 rounded"
                                      title="Reply"
                                    >
                                      <Reply className="w-4 h-4 text-gray-500" />
                                    </button>
                                    {isOwn && (
                                      <>
                                        <button
                                          onClick={() => {
                                            setEditingMessage(message);
                                            setNewMessage(message.content);
                                            messageInputRef.current?.focus();
                                          }}
                                          className="p-1 hover:bg-gray-100 rounded"
                                          title="Edit"
                                        >
                                          <Edit3 className="w-4 h-4 text-gray-500" />
                                        </button>
                                        <button
                                          onClick={() => deleteMessage(message.id)}
                                          className="p-1 hover:bg-gray-100 rounded"
                                          title="Delete"
                                        >
                                          <Trash2 className="w-4 h-4 text-red-500" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )}

                                {/* Emoji Picker */}
                                {showEmojiPicker === message.id && (
                                  <div className={`absolute z-10 ${isOwn ? 'right-0' : 'left-0'} top-full mt-1 bg-white shadow-lg rounded-lg p-2 flex gap-1`}>
                                    {commonEmojis.map(emoji => (
                                      <button
                                        key={emoji}
                                        onClick={() => {
                                          addReaction(message.id, emoji);
                                          setShowEmojiPicker(false);
                                        }}
                                        className="hover:bg-gray-100 p-1 rounded text-lg"
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Reply Preview */}
                {replyTo && (
                  <div className="px-4 py-2 bg-gray-100 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Reply className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-xs font-medium text-collaborb-primary">
                          Replying to {replyTo.senderName}
                        </p>
                        <p className="text-sm text-gray-600 truncate max-w-md">
                          {replyTo.content}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setReplyTo(null)}>
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                )}

                {/* Edit Preview */}
                {editingMessage && (
                  <div className="px-4 py-2 bg-yellow-50 border-t border-yellow-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Edit3 className="w-4 h-4 text-yellow-600" />
                      <p className="text-sm text-yellow-800">Editing message</p>
                    </div>
                    <button onClick={() => {
                      setEditingMessage(null);
                      setNewMessage('');
                    }}>
                      <X className="w-4 h-4 text-yellow-600" />
                    </button>
                  </div>
                )}

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-gray-100 rounded-full"
                      title="Attach file"
                    >
                      <Paperclip className="w-5 h-5 text-gray-500" />
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="p-2 hover:bg-gray-100 rounded-full"
                      title="Send image"
                    >
                      <Image className="w-5 h-5 text-gray-500" />
                    </button>

                    <div className="flex-1 relative">
                      <textarea
                        ref={messageInputRef}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Type a message..."
                        rows={1}
                        className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-collaborb-primary focus:border-transparent resize-none text-gray-800 placeholder:text-gray-400"
                        style={{ maxHeight: '120px' }}
                      />
                    </div>

                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim()}
                      className={`p-3 rounded-full transition ${
                        newMessage.trim()
                          ? 'bg-collaborb-primary text-white hover:bg-collaborb-primary-dark'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              // No chat selected
              <div className="flex-1 flex flex-col items-center justify-center text-gray-500 bg-gray-50">
                <MessageSquare className="w-24 h-24 mb-4 text-gray-300" />
                <h3 className="text-xl font-medium mb-2">Welcome to CollabOrb Chat</h3>
                <p className="text-sm">Select a conversation or start a new one</p>
              </div>
            )}
          </div>
        )}

        {/* Room Settings Modal */}
        {showRoomSettings && selectedRoom && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg p-6 w-80">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Chat Settings</h3>

              <div className="space-y-4">
                <button
                  onClick={() => updateRoomSettings({ isPinned: !selectedRoom.isPinned })}
                  className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 rounded text-gray-800"
                >
                  <Pin className="w-5 h-5 text-gray-600" />
                  {selectedRoom.isPinned ? 'Unpin Chat' : 'Pin Chat'}
                </button>

                <button
                  onClick={() => updateRoomSettings({ isMuted: !selectedRoom.isMuted })}
                  className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 rounded text-gray-800"
                >
                  <BellOff className="w-5 h-5 text-gray-600" />
                  {selectedRoom.isMuted ? 'Unmute Chat' : 'Mute Chat'}
                </button>

                <button
                  onClick={() => updateRoomSettings({ isArchived: true })}
                  className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 rounded text-gray-800"
                >
                  <Archive className="w-5 h-5 text-gray-600" />
                  Archive Chat
                </button>

                {selectedRoom.type === 'GROUP' && (
                  <button
                    onClick={() => {/* Show add participants modal */}}
                    className="w-full flex items-center gap-3 p-2 hover:bg-gray-100 rounded text-gray-800"
                  >
                    <Users className="w-5 h-5 text-gray-600" />
                    Add Participants
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowRoomSettings(false)}
                className="mt-4 w-full py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* New Chat Modal */}
        {showNewChatModal && (
          <NewChatModal
            onClose={() => setShowNewChatModal(false)}
            onStartDirectChat={startDirectChat}
            onCreateGroupChat={createGroupChat}
            currentUserId={user?.userId}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

/**
 * New Chat Modal Component
 */
const NewChatModal = ({ onClose, onStartDirectChat, onCreateGroupChat, currentUserId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isGroupChat, setIsGroupChat] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  // Search users
  const searchUsers = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setLoading(true);
      // Use the chat user search API
      const response = await api.get(`/chat/users/search?userId=${currentUserId}&query=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => searchUsers(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle user selection
  const toggleUserSelection = (user) => {
    if (selectedUsers.find(u => u.userId === user.userId)) {
      setSelectedUsers(prev => prev.filter(u => u.userId !== user.userId));
    } else {
      setSelectedUsers(prev => [...prev, user]);
    }
  };

  // Start chat
  const handleStartChat = () => {
    if (isGroupChat) {
      if (!groupName.trim() || selectedUsers.length < 2) return;
      onCreateGroupChat(groupName, selectedUsers.map(u => u.userId));
    } else {
      if (selectedUsers.length !== 1) return;
      onStartDirectChat(selectedUsers[0].userId);
    }
  };

  return (
    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
      <div className="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">New Conversation</h3>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Toggle Group/Direct */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setIsGroupChat(false)}
            className={`flex-1 py-2 rounded-lg transition ${
              !isGroupChat 
                ? 'bg-collaborb-primary text-white' 
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Direct Message
          </button>
          <button
            onClick={() => setIsGroupChat(true)}
            className={`flex-1 py-2 rounded-lg transition ${
              isGroupChat 
                ? 'bg-collaborb-primary text-white' 
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            Group Chat
          </button>
        </div>

        {/* Group Name (if group chat) */}
        {isGroupChat && (
          <input
            type="text"
            placeholder="Group name..."
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-collaborb-primary"
          />
        )}

        {/* Search Users */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-collaborb-primary"
          />
        </div>

        {/* Selected Users */}
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedUsers.map(user => (
              <span
                key={user.userId}
                className="bg-collaborb-primary text-white px-3 py-1 rounded-full text-sm flex items-center gap-1"
              >
                {user.name}
                <button onClick={() => toggleUserSelection(user)}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search Results */}
        <div className="flex-1 overflow-y-auto mb-4">
          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-collaborb-primary mx-auto"></div>
            </div>
          ) : searchResults.length === 0 && searchQuery ? (
            <p className="text-center text-gray-500 py-4">No users found</p>
          ) : (
            searchResults.map(user => (
              <div
                key={user.userId}
                onClick={() => toggleUserSelection(user)}
                className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-lg ${
                  selectedUsers.find(u => u.userId === user.userId) ? 'bg-collaborb-primary/10' : ''
                }`}
              >
                <img
                  src={user.avatarUrl || '/default-avatar.png'}
                  alt={user.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-800">{user.name}</p>
                  <p className="text-sm text-gray-500">{user.role}</p>
                </div>
                {selectedUsers.find(u => u.userId === user.userId) && (
                  <Check className="w-5 h-5 text-collaborb-primary ml-auto" />
                )}
              </div>
            ))
          )}
        </div>

        {/* Start Chat Button */}
        <button
          onClick={handleStartChat}
          disabled={
            isGroupChat
              ? !groupName.trim() || selectedUsers.length < 2
              : selectedUsers.length !== 1
          }
          className={`w-full py-3 rounded-lg transition ${
            (isGroupChat ? groupName.trim() && selectedUsers.length >= 2 : selectedUsers.length === 1)
              ? 'bg-collaborb-primary text-white hover:bg-collaborb-primary-dark'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isGroupChat ? 'Create Group' : 'Start Chat'}
        </button>
      </div>
    </div>
  );
};

export default ChatEngine;


